import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Error,
  Add,
  Remove,
  SwapHoriz,
  ExpandMore,
  Person,
  Star,
} from '@mui/icons-material';

const MetricCard = ({ title, value, icon: Icon, color = 'primary' }) => {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={1}>
          <Icon color={color} sx={{ mr: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div">
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

const MetricsPanel = ({ metrics, speakerDifferences, referenceMode }) => {
  if (!metrics) {
    return null;
  }

  const getReferenceInfo = () => {
    if (referenceMode === 'a') {
      return {
        text: 'WER calculated relative to Transcript A (ground truth)',
        severity: 'info',
      };
    } else if (referenceMode === 'b') {
      return {
        text: 'WER calculated relative to Transcript B (ground truth)',
        severity: 'info',
      };
    } else {
      return {
        text: 'Symmetric mode: No reference selected. WER calculated relative to longer transcript.',
        severity: 'warning',
      };
    }
  };

  const referenceInfo = getReferenceInfo();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Comparison Metrics
        </Typography>
        {referenceMode && (
          <Chip
            label={`Reference: Transcript ${referenceMode.toUpperCase()}`}
            color="primary"
            size="small"
            icon={<Star />}
          />
        )}
      </Box>

      <Alert severity={referenceInfo.severity} sx={{ mb: 2 }}>
        {referenceInfo.text}
      </Alert>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Agreement"
            value={`${metrics.agreement_percentage.toFixed(1)}%`}
            icon={CheckCircle}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Matches"
            value={metrics.matches}
            icon={CheckCircle}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Words A"
            value={metrics.total_words_a}
            icon={Person}
            color="info"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Words B"
            value={metrics.total_words_b}
            icon={Person}
            color="info"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={3}>
          <MetricCard
            title="Word Error Rate"
            value={`${metrics.word_error_rate.toFixed(1)}%`}
            icon={Error}
            color="error"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <MetricCard
            title="Insertions"
            value={metrics.insertions}
            icon={Add}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <MetricCard
            title="Deletions"
            value={metrics.deletions}
            icon={Remove}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <MetricCard
            title="Substitutions"
            value={metrics.substitutions}
            icon={SwapHoriz}
            color="warning"
          />
        </Grid>
      </Grid>

      {speakerDifferences && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="subtitle1">Speaker Differences</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Transcript A: {speakerDifferences.segments_with_speakers_a} segments with speakers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Speaker transitions: {speakerDifferences.speaker_transitions_a}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Transcript B: {speakerDifferences.segments_with_speakers_b} segments with speakers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Speaker transitions: {speakerDifferences.speaker_transitions_b}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Speaker boundary agreement: {speakerDifferences.speaker_boundary_agreement.toFixed(1)}%
                </Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default MetricsPanel;
